import Decimal from 'decimal.js';
import { z } from 'zod';
import {
  AccountCategorySchema,
  AccountCodeSchema,
  ReportMetaSchema,
  type AccountCategory,
} from './accounting';
import { BudgetPeriodEntrySchema } from './budgets';
import { BusinessCodeSchema, RecordIdSchema } from './identifiers';
import { LocalizedLabelSchema } from './localized';
import { DecimalStringSchema, type DecimalString } from './money';
import { BusinessDateSchema, InstantSchema, addDays, addMonths, type BusinessDate } from './time';

/**
 * Company financial statements (FIN-REPORT-009 … FIN-REPORT-013, owner-approved 2026-10-17).
 *
 * The ledger is the only source. An account's **category** (asset, liability, equity, revenue,
 * expense) decides which statement it belongs to and is authoritative; its **statement line** — set
 * by an authorized person on the chart of accounts — only places it on a line inside that statement
 * (cost of revenue, payroll, depreciation …). A line that is not classified is shown as unclassified,
 * never dropped and never guessed, and a subtotal that depends on it (gross profit) says it cannot be
 * determined instead of printing a misleading figure.
 */

/* ------------------------------------------------------------ statement lines */

/** Every statement line, with the one category it may be used on. */
export const FINANCIAL_LINE_CATEGORY = {
  // Assets — current
  cash: 'asset',
  receivable: 'asset',
  inventory: 'asset',
  prepayment: 'asset',
  otherCurrentAsset: 'asset',
  // Assets — non-current
  fixedAsset: 'asset',
  accumulatedDepreciation: 'asset',
  otherNonCurrentAsset: 'asset',
  // Liabilities — current
  payable: 'liability',
  accruedLiability: 'liability',
  contractLiability: 'liability',
  taxPayable: 'liability',
  otherCurrentLiability: 'liability',
  // Liabilities — non-current
  borrowing: 'liability',
  otherNonCurrentLiability: 'liability',
  // Equity
  capital: 'equity',
  ownerContribution: 'equity',
  drawings: 'equity',
  distribution: 'equity',
  retainedEarnings: 'equity',
  otherEquity: 'equity',
  // Revenue
  operatingRevenue: 'revenue',
  otherIncome: 'revenue',
  // Expenses
  costOfRevenue: 'expense',
  operatingExpense: 'expense',
  payrollExpense: 'expense',
  depreciation: 'expense',
  financeCost: 'expense',
  incomeTax: 'expense',
  otherExpense: 'expense',
} as const satisfies Record<string, AccountCategory>;

export type FinancialLine = keyof typeof FINANCIAL_LINE_CATEGORY;
export const FINANCIAL_LINES = Object.keys(FINANCIAL_LINE_CATEGORY) as [
  FinancialLine,
  ...FinancialLine[],
];
export const FinancialLineSchema = z.enum(FINANCIAL_LINES);

/** The lines an account of this category may carry, in statement order. */
export function financialLinesFor(category: AccountCategory): FinancialLine[] {
  return FINANCIAL_LINES.filter((line) => FINANCIAL_LINE_CATEGORY[line] === category);
}

/** True when the line belongs to the category: a revenue account cannot be classified as cash. */
export function financialLineFits(line: FinancialLine, category: AccountCategory): boolean {
  return FINANCIAL_LINE_CATEGORY[line] === category;
}

export const CURRENT_ASSET_LINES: readonly FinancialLine[] = [
  'cash',
  'receivable',
  'inventory',
  'prepayment',
  'otherCurrentAsset',
];
export const CURRENT_LIABILITY_LINES: readonly FinancialLine[] = [
  'payable',
  'accruedLiability',
  'contractLiability',
  'taxPayable',
  'otherCurrentLiability',
];
/** The operating expenses of the income statement, below gross profit. */
export const OPERATING_EXPENSE_LINES: readonly FinancialLine[] = [
  'operatingExpense',
  'payrollExpense',
  'depreciation',
];

/* -------------------------------------------------------------- cash flow */

export const CASH_FLOW_ACTIVITIES = ['operating', 'investing', 'financing'] as const;
export const CashFlowActivitySchema = z.enum(CASH_FLOW_ACTIVITIES);
export type CashFlowActivity = z.infer<typeof CashFlowActivitySchema>;

/**
 * The activity a cash movement belongs to, read from the **other side** of its entry. Buying or
 * selling a long-lived asset is investing; owners' money and long-term borrowing are financing;
 * everything else — customers, suppliers, salaries, taxes, interest — is operating (interest paid is
 * operating, one of the two presentations IAS 7 permits). An unclassified counter account is shown as
 * operating and flagged, never hidden.
 */
export function cashFlowActivityOf(line: FinancialLine | undefined): CashFlowActivity {
  switch (line) {
    case 'fixedAsset':
    case 'accumulatedDepreciation':
    case 'otherNonCurrentAsset':
      return 'investing';
    case 'capital':
    case 'ownerContribution':
    case 'drawings':
    case 'distribution':
    case 'borrowing':
      return 'financing';
    default:
      return 'operating';
  }
}

/* ------------------------------------------------------------- periods */

export const PERIOD_PRESETS = ['month', 'quarter', 'year', 'yearToDate', 'custom'] as const;
export const PeriodPresetSchema = z.enum(PERIOD_PRESETS);
export type PeriodPreset = z.infer<typeof PeriodPresetSchema>;

export const COMPARISONS = ['none', 'previousPeriod', 'previousYear'] as const;
export const ComparisonSchema = z.enum(COMPARISONS);
export type Comparison = z.infer<typeof ComparisonSchema>;

export interface DateRange {
  from: BusinessDate;
  to: BusinessDate;
}

function monthStart(date: BusinessDate): BusinessDate {
  return BusinessDateSchema.parse(`${date.slice(0, 7)}-01`);
}

/**
 * The range a preset covers around `anchor`. Quarters and years follow the fiscal year
 * (`fiscalStartMonth`, 1–12); `yearToDate` runs from the fiscal year's first day to the anchor itself.
 */
export function periodRange(
  preset: Exclude<PeriodPreset, 'custom'>,
  anchor: BusinessDate,
  fiscalStartMonth: number,
): DateRange {
  const month = Number(anchor.slice(5, 7));
  const thisMonth = monthStart(anchor);
  if (preset === 'month') {
    return { from: thisMonth, to: addDays(addMonths(thisMonth, 1), -1) };
  }
  // Months since the fiscal year started, 0–11.
  const intoYear = (month - fiscalStartMonth + 12) % 12;
  const yearStart = addMonths(thisMonth, -intoYear);
  if (preset === 'yearToDate') return { from: yearStart, to: anchor };
  if (preset === 'year') return { from: yearStart, to: addDays(addMonths(yearStart, 12), -1) };
  const quarterStart = addMonths(yearStart, Math.floor(intoYear / 3) * 3);
  return { from: quarterStart, to: addDays(addMonths(quarterStart, 3), -1) };
}

/** Whole calendar months a range covers exactly, or undefined when it is not month-aligned. */
function wholeMonths(range: DateRange): number | undefined {
  if (range.from.slice(8) !== '01') return undefined;
  const end = addDays(range.to, 1);
  if (end.slice(8) !== '01') return undefined;
  const months =
    (Number(end.slice(0, 4)) - Number(range.from.slice(0, 4))) * 12 +
    (Number(end.slice(5, 7)) - Number(range.from.slice(5, 7)));
  return months > 0 ? months : undefined;
}

/**
 * The like-for-like range to compare with. The previous year is the same days one year earlier — a
 * year-to-date compares with the same stretch of last year, never the whole of it. The previous period
 * is the stretch of equal length immediately before; month-aligned ranges step by whole months, so
 * March compares with February, not with the 28 days before 1 March.
 */
export function comparisonRange(range: DateRange, comparison: Comparison): DateRange | undefined {
  if (comparison === 'none') return undefined;
  if (comparison === 'previousYear') {
    return { from: addMonths(range.from, -12), to: addMonths(range.to, -12) };
  }
  const months = wholeMonths(range);
  if (months !== undefined) {
    const from = addMonths(range.from, -months);
    return { from, to: addDays(range.from, -1) };
  }
  const days =
    (Date.parse(`${range.to}T00:00:00Z`) - Date.parse(`${range.from}T00:00:00Z`)) / 86_400_000 + 1;
  return { from: addDays(range.from, -days), to: addDays(range.from, -1) };
}

/* -------------------------------------------------------------- growth */

/**
 * Why a percentage is or is not shown. `comparable`: both positive. `zeroBase`: nothing to divide by.
 * `negativeBase`: a percentage of a loss is not growth. `signChange`: from loss to profit or back — a
 * percentage would mislead, the absolute change says it. `noComparison`: no comparison was asked for.
 */
export const GROWTH_BASES = [
  'comparable',
  'zeroBase',
  'negativeBase',
  'signChange',
  'noComparison',
] as const;
export const GrowthBasisSchema = z.enum(GROWTH_BASES);

export const GrowthSchema = z.strictObject({
  previous: DecimalStringSchema.optional(),
  change: DecimalStringSchema.optional(),
  /** Two decimals, present only when `basis` is `comparable`. */
  percent: DecimalStringSchema.optional(),
  basis: GrowthBasisSchema,
});
export type Growth = z.infer<typeof GrowthSchema>;

const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_EVEN });

/** Absolute and relative change, refusing the percentages that would mislead (see `GROWTH_BASES`). */
export function growth(current: string, previous: string | undefined, scale = 2): Growth {
  if (previous === undefined) return { basis: 'noComparison' };
  const now = new D(current);
  const before = new D(previous);
  const change = now.minus(before).toFixed(scale) as DecimalString;
  const base = { previous: before.toFixed(scale) as DecimalString, change };
  if (before.isZero()) return { ...base, basis: 'zeroBase' };
  if (before.isNegative() && now.isNegative()) return { ...base, basis: 'negativeBase' };
  if (before.isNegative() !== now.isNegative() && !now.isZero()) {
    return { ...base, basis: 'signChange' };
  }
  if (before.isNegative()) return { ...base, basis: 'negativeBase' };
  const percent = now.minus(before).dividedBy(before).times(100).toFixed(2) as DecimalString;
  return { ...base, percent, basis: 'comparable' };
}

/** A ratio in percent with two decimals, or undefined when the base is not positive. */
export function marginPercent(part: string, whole: string): DecimalString | undefined {
  const base = new D(whole);
  if (!base.isPositive() || base.isZero()) return undefined;
  return new D(part).dividedBy(base).times(100).toFixed(2) as DecimalString;
}

/* ------------------------------------------------------------ queries */

const DimensionShape = {
  branchId: RecordIdSchema.optional(),
  projectId: RecordIdSchema.optional(),
  departmentId: RecordIdSchema.optional(),
  costCenterCode: BusinessCodeSchema.optional(),
};

export const IncomeStatementQuerySchema = z
  .strictObject({
    legalEntityId: RecordIdSchema,
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    compare: ComparisonSchema.default('none'),
    ...DimensionShape,
  })
  .refine((value) => value.from <= value.to, { message: 'RANGE_REVERSED' });
export type IncomeStatementQuery = z.infer<typeof IncomeStatementQuerySchema>;

export const PositionQuerySchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  asOf: BusinessDateSchema,
  compare: ComparisonSchema.default('none'),
});
export type PositionQuery = z.infer<typeof PositionQuerySchema>;

export const CashFlowQuerySchema = z
  .strictObject({
    legalEntityId: RecordIdSchema,
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    compare: ComparisonSchema.default('none'),
  })
  .refine((value) => value.from <= value.to, { message: 'RANGE_REVERSED' });
export type CashFlowQuery = z.infer<typeof CashFlowQuerySchema>;

/* ------------------------------------------------------- completeness */

/**
 * What a reader must know before trusting a statement: accounts that are not classified, business
 * events still waiting for a posting rule, opening balances never posted, a revenue-recognition rule
 * not adopted, a slice hidden by the reader's scope. Each item names where it is fixed.
 */
export const STATEMENT_ISSUES = [
  'unclassifiedAccounts',
  'pendingPostingEvents',
  'openingBalancesMissing',
  'revenueRecognitionNotAdopted',
  'unrecognizedRevenue',
  'noCashAccounts',
  'scopeLimited',
  'notBalanced',
] as const;
export const StatementIssueCodeSchema = z.enum(STATEMENT_ISSUES);
export type StatementIssueCode = z.infer<typeof StatementIssueCodeSchema>;

export const StatementIssueSchema = z.strictObject({
  code: StatementIssueCodeSchema,
  count: z.number().int().nonnegative().optional(),
  amount: DecimalStringSchema.optional(),
  /** Where it is fixed, as an application path. */
  link: z.string().max(200).optional(),
});
export type StatementIssue = z.infer<typeof StatementIssueSchema>;

export const UnclassifiedAccountSchema = z.strictObject({
  accountId: RecordIdSchema,
  code: AccountCodeSchema,
  name: LocalizedLabelSchema,
  category: AccountCategorySchema,
});

/* ------------------------------------------------------- shared shapes */

export const StatementAccountRowSchema = z.strictObject({
  accountId: RecordIdSchema,
  code: AccountCodeSchema,
  name: LocalizedLabelSchema,
  amount: DecimalStringSchema,
  previous: DecimalStringSchema.optional(),
});
export type StatementAccountRow = z.infer<typeof StatementAccountRowSchema>;

/** One line of a statement: classified (`line`) or the category's unclassified accounts. */
export const StatementSectionSchema = z.strictObject({
  line: z.union([FinancialLineSchema, z.enum(['unclassifiedRevenue', 'unclassifiedExpense'])]),
  accounts: z.array(StatementAccountRowSchema),
  total: DecimalStringSchema,
  previous: DecimalStringSchema.optional(),
});

/** A figure with its comparison. `amount` absent means it cannot be determined (see completeness). */
export const StatementFigureSchema = z.strictObject({
  amount: DecimalStringSchema.optional(),
  growth: GrowthSchema,
});
export type StatementFigure = z.infer<typeof StatementFigureSchema>;

const ComparisonRangeSchema = z.strictObject({
  mode: ComparisonSchema,
  from: BusinessDateSchema,
  to: BusinessDateSchema,
});

/* ----------------------------------------------------- income statement */

export const IncomeStatementSchema = z.strictObject({
  meta: ReportMetaSchema,
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  comparison: ComparisonRangeSchema.optional(),
  /** Revenue sections, then expense sections, in statement order; empty lines are left out. */
  revenue: z.array(StatementSectionSchema),
  expenses: z.array(StatementSectionSchema),
  totals: z.strictObject({
    operatingRevenue: StatementFigureSchema,
    costOfRevenue: StatementFigureSchema,
    grossProfit: StatementFigureSchema,
    operatingExpenses: StatementFigureSchema,
    operatingProfit: StatementFigureSchema,
    otherIncome: StatementFigureSchema,
    otherExpenses: StatementFigureSchema,
    financeCost: StatementFigureSchema,
    profitBeforeTax: StatementFigureSchema,
    incomeTax: StatementFigureSchema,
    totalRevenue: StatementFigureSchema,
    totalExpenses: StatementFigureSchema,
    netProfit: StatementFigureSchema,
  }),
  margins: z.strictObject({
    gross: DecimalStringSchema.optional(),
    net: DecimalStringSchema.optional(),
  }),
  unclassified: z.array(UnclassifiedAccountSchema),
  issues: z.array(StatementIssueSchema),
});
export type IncomeStatement = z.infer<typeof IncomeStatementSchema>;

/* -------------------------------------------------- financial position */

export const PositionGroupSchema = z.strictObject({
  sections: z.array(StatementSectionSchema.extend({ line: z.string() })),
  total: DecimalStringSchema,
  previous: DecimalStringSchema.optional(),
});

export const FinancialPositionSchema = z.strictObject({
  meta: ReportMetaSchema,
  asOf: BusinessDateSchema,
  comparison: z.strictObject({ mode: ComparisonSchema, asOf: BusinessDateSchema }).optional(),
  currentAssets: PositionGroupSchema,
  nonCurrentAssets: PositionGroupSchema,
  unclassifiedAssets: PositionGroupSchema,
  totalAssets: StatementFigureSchema,
  currentLiabilities: PositionGroupSchema,
  nonCurrentLiabilities: PositionGroupSchema,
  unclassifiedLiabilities: PositionGroupSchema,
  totalLiabilities: StatementFigureSchema,
  equity: PositionGroupSchema,
  /** Revenue less expenses never closed into equity: what the ledger has earned to date. */
  accumulatedEarnings: StatementFigureSchema,
  totalEquity: StatementFigureSchema,
  totalLiabilitiesAndEquity: StatementFigureSchema,
  balanced: z.boolean(),
  difference: DecimalStringSchema,
  unclassified: z.array(UnclassifiedAccountSchema),
  issues: z.array(StatementIssueSchema),
});
export type FinancialPosition = z.infer<typeof FinancialPositionSchema>;

/* ------------------------------------------------------------ cash flow */

export const CashFlowLineSchema = z.strictObject({
  line: z.union([FinancialLineSchema, z.literal('unclassified')]),
  inflow: DecimalStringSchema,
  outflow: DecimalStringSchema,
  net: DecimalStringSchema,
  previous: DecimalStringSchema.optional(),
});

export const CashFlowStatementSchema = z.strictObject({
  meta: ReportMetaSchema,
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  comparison: ComparisonRangeSchema.optional(),
  openingCash: DecimalStringSchema,
  closingCash: DecimalStringSchema,
  activities: z.array(
    z.strictObject({
      activity: CashFlowActivitySchema,
      lines: z.array(CashFlowLineSchema),
      net: StatementFigureSchema,
    }),
  ),
  netChange: StatementFigureSchema,
  /** Opening cash plus the net change equals closing cash. False only for a scope-limited reader. */
  reconciled: z.boolean(),
  difference: DecimalStringSchema,
  cashAccounts: z.array(
    z.strictObject({
      accountId: RecordIdSchema,
      code: AccountCodeSchema,
      name: LocalizedLabelSchema,
      opening: DecimalStringSchema,
      closing: DecimalStringSchema,
    }),
  ),
  unclassified: z.array(UnclassifiedAccountSchema),
  issues: z.array(StatementIssueSchema),
});
export type CashFlowStatement = z.infer<typeof CashFlowStatementSchema>;

/* ------------------------------------------------------ executive overview */

export const FinancialOverviewSchema = z.strictObject({
  meta: ReportMetaSchema,
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  comparison: ComparisonRangeSchema.optional(),
  revenue: StatementFigureSchema,
  expenses: StatementFigureSchema,
  grossProfit: StatementFigureSchema,
  netProfit: StatementFigureSchema,
  netMargin: DecimalStringSchema.optional(),
  /** Balances at `to`, from accounts classified as cash, receivable and payable. */
  cash: DecimalStringSchema.optional(),
  receivables: DecimalStringSchema.optional(),
  payables: DecimalStringSchema.optional(),
  /** The twelve months ending with `to`, oldest first. */
  trend: z.array(
    z.strictObject({
      month: z.string().regex(/^\d{4}-\d{2}$/),
      revenue: DecimalStringSchema,
      expenses: DecimalStringSchema,
      netProfit: DecimalStringSchema,
    }),
  ),
  issues: z.array(StatementIssueSchema),
});
export type FinancialOverview = z.infer<typeof FinancialOverviewSchema>;

/* ------------------------------------------------------ statement files */

export const STATEMENT_KINDS = ['incomeStatement', 'financialPosition', 'cashFlow'] as const;
export const StatementKindSchema = z.enum(STATEMENT_KINDS);
export type StatementKind = z.infer<typeof StatementKindSchema>;

export const STATEMENT_FILE_FORMATS = ['pdf', 'xlsx', 'csv'] as const;

export const StatementFileQuerySchema = z
  .strictObject({
    legalEntityId: RecordIdSchema,
    from: BusinessDateSchema.optional(),
    to: BusinessDateSchema.optional(),
    asOf: BusinessDateSchema.optional(),
    compare: ComparisonSchema.default('none'),
    format: z.enum(STATEMENT_FILE_FORMATS),
    locale: z.enum(['ar', 'en']).default('ar'),
    ...DimensionShape,
  })
  .refine((value) => !(value.from && value.to) || value.from <= value.to, {
    message: 'RANGE_REVERSED',
  });
export type StatementFileQuery = z.infer<typeof StatementFileQuerySchema>;

/* ---------------------------------------------- company performance (2026-10-17) */

/**
 * The performance of the company itself (owner-approved 2026-10-17): one period against the previous
 * equivalent period and the same period last year, with margins, the approved budget, and the ledger
 * lines behind every figure. Built from the same income statements as the statements screen — posted
 * ledger lines, placed by their statement classification — summed across the legal entities the reader
 * may see, or one of them. Entities in different currencies are **not** added together: no conversion
 * policy exists, so the company total is then unavailable and each entity must be read on its own.
 *
 * Each figure says whether it is `available`, has `noActivity` in the period (nothing posted), or is
 * `notConfigured` (it depends on accounts without a statement line, or the chart has none). Growth uses
 * `growth()`: no percentage on a zero or negative base, and a sign change is labelled as such.
 */
export const CompanyPerformanceQuerySchema = z
  .strictObject({
    /** Absent: every legal entity the reader may see (one currency only). */
    legalEntityId: RecordIdSchema.optional(),
    preset: PeriodPresetSchema.default('custom'),
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    branchId: RecordIdSchema.optional(),
    projectId: RecordIdSchema.optional(),
  })
  .refine((value) => value.from <= value.to, { message: 'RANGE_REVERSED' });
export type CompanyPerformanceQuery = z.infer<typeof CompanyPerformanceQuerySchema>;

export const PERFORMANCE_STATUSES = ['available', 'noActivity', 'notConfigured'] as const;
export const PerformanceStatusSchema = z.enum(PERFORMANCE_STATUSES);
export type PerformanceStatus = z.infer<typeof PerformanceStatusSchema>;

/** A profit that changed sign: a percentage would mislead, so the change is named instead. */
export const PROFIT_TRANSITIONS = ['lossToProfit', 'profitToLoss'] as const;

export const PerformanceComparisonSchema = z.strictObject({
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  amount: DecimalStringSchema.optional(),
  status: PerformanceStatusSchema,
  change: DecimalStringSchema.optional(),
  percent: DecimalStringSchema.optional(),
  basis: GrowthBasisSchema,
  transition: z.enum(PROFIT_TRANSITIONS).optional(),
});
export type PerformanceComparison = z.infer<typeof PerformanceComparisonSchema>;

export const PerformanceFigureSchema = z.strictObject({
  amount: DecimalStringSchema.optional(),
  status: PerformanceStatusSchema,
  previousPeriod: PerformanceComparisonSchema,
  sameLastYear: PerformanceComparisonSchema,
});
export type PerformanceFigure = z.infer<typeof PerformanceFigureSchema>;

export const PERFORMANCE_FIGURES = [
  'revenue',
  'costOfRevenue',
  'grossProfit',
  'operatingExpenses',
  'operatingProfit',
  'otherIncome',
  'otherExpenses',
  'financeCost',
  'incomeTax',
  'netProfit',
] as const;
export type PerformanceFigureKey = (typeof PERFORMANCE_FIGURES)[number];
/** Figures that are profits: their sign change is labelled. */
export const PROFIT_FIGURES: readonly PerformanceFigureKey[] = [
  'grossProfit',
  'operatingProfit',
  'netProfit',
];

const MarginSetSchema = z.strictObject({
  current: DecimalStringSchema.optional(),
  previousPeriod: DecimalStringSchema.optional(),
  sameLastYear: DecimalStringSchema.optional(),
});

export const PerformanceAccountSchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  accountId: RecordIdSchema,
  code: AccountCodeSchema,
  name: LocalizedLabelSchema,
  amount: DecimalStringSchema,
  previousPeriod: DecimalStringSchema,
  sameLastYear: DecimalStringSchema,
});

export const PerformanceLineSchema = z.strictObject({
  line: z.union([FinancialLineSchema, z.enum(['unclassifiedRevenue', 'unclassifiedExpense'])]),
  amount: DecimalStringSchema,
  previousPeriod: DecimalStringSchema,
  sameLastYear: DecimalStringSchema,
  accounts: z.array(PerformanceAccountSchema),
});
export type PerformanceLine = z.infer<typeof PerformanceLineSchema>;

export const PERFORMANCE_UNAVAILABLE = ['currencyMismatch', 'noEntity'] as const;
export const BUDGET_PERFORMANCE_STATUSES = [
  'available',
  'noBudget',
  'notPhased',
  'currencyMismatch',
] as const;

const PerformanceFiguresSchema = z.strictObject({
  revenue: PerformanceFigureSchema,
  costOfRevenue: PerformanceFigureSchema,
  grossProfit: PerformanceFigureSchema,
  operatingExpenses: PerformanceFigureSchema,
  operatingProfit: PerformanceFigureSchema,
  otherIncome: PerformanceFigureSchema,
  otherExpenses: PerformanceFigureSchema,
  financeCost: PerformanceFigureSchema,
  incomeTax: PerformanceFigureSchema,
  netProfit: PerformanceFigureSchema,
});

export const CompanyPerformanceSchema = z.strictObject({
  meta: z.strictObject({
    generatedAt: InstantSchema,
    /** The single currency every figure is in; absent when the entities' currencies differ. */
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .optional(),
    scopeLimited: z.boolean(),
    entities: z.array(
      z.strictObject({
        legalEntityId: RecordIdSchema,
        code: z.string(),
        name: LocalizedLabelSchema,
        currency: z.string(),
      }),
    ),
  }),
  preset: PeriodPresetSchema,
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  previousPeriod: z.strictObject({ from: BusinessDateSchema, to: BusinessDateSchema }),
  sameLastYear: z.strictObject({ from: BusinessDateSchema, to: BusinessDateSchema }),
  availability: z.enum(['available', 'unavailable']),
  unavailableReason: z.enum(PERFORMANCE_UNAVAILABLE).optional(),
  figures: PerformanceFiguresSchema.optional(),
  margins: z
    .strictObject({ gross: MarginSetSchema, operating: MarginSetSchema, net: MarginSetSchema })
    .optional(),
  /** Every statement line with its accounts, for the drill-down to the general ledger. */
  lines: z.array(PerformanceLineSchema),
  budget: z.strictObject({
    status: z.enum(BUDGET_PERFORMANCE_STATUSES),
    budgets: z.array(BudgetPeriodEntrySchema),
    totals: z
      .strictObject({
        budgeted: DecimalStringSchema,
        actual: DecimalStringSchema,
        variance: DecimalStringSchema,
        usedPercent: z.string().optional(),
      })
      .optional(),
  }),
  issues: z.array(StatementIssueSchema.extend({ legalEntityId: RecordIdSchema.optional() })),
});
export type CompanyPerformance = z.infer<typeof CompanyPerformanceSchema>;

export const CompanyPerformanceFileQuerySchema = z
  .strictObject({
    legalEntityId: RecordIdSchema.optional(),
    preset: PeriodPresetSchema.default('custom'),
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    branchId: RecordIdSchema.optional(),
    projectId: RecordIdSchema.optional(),
    format: z.enum(STATEMENT_FILE_FORMATS),
    locale: z.enum(['ar', 'en']).default('ar'),
  })
  .refine((value) => value.from <= value.to, { message: 'RANGE_REVERSED' });
export type CompanyPerformanceFileQuery = z.infer<typeof CompanyPerformanceFileQuerySchema>;

/** The transition a profit made between two periods, when its sign changed. */
export function profitTransition(
  current: string | undefined,
  previous: string | undefined,
): (typeof PROFIT_TRANSITIONS)[number] | undefined {
  if (current === undefined || previous === undefined) return undefined;
  const now = new D(current);
  const before = new D(previous);
  if (before.isNegative() && !now.isNegative() && !now.isZero()) return 'lossToProfit';
  if (!before.isNegative() && !before.isZero() && now.isNegative()) return 'profitToLoss';
  return undefined;
}
